import { logUnknownException } from '@local/error-logging';
import EmptyState from '@local/web-design-system-2/dist/components/EmptyState/EmptyState';
import TableSkeleton from '@local/web-design-system-2/dist/components/TableSkeleton/TableSkeleton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Fragment, type ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';

import { DataTableBodyCell } from './data-table-body-cell/DataTableBodyCell';
import { i18n as bodyCellI18n } from './data-table-body-cell/DataTableBodyCell.i18n';
import { DataTableFilterBar } from './data-table-filter-bar/DataTableFilterBar';
import { DataTableHeaderCell } from './data-table-header-cell/DataTableHeaderCell';
import { i18n } from './DataTable.i18n';
import { useStyles } from './DataTable.styles';
import {
    DEFAULT_DATA_TABLE_STATE,
    SortDirection,
    toCellString,
    type DataTableCellShell,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableQueryState,
    type DataTableRow,
    type DataTableSort,
    type DataTableState,
    type OnRowClick,
} from './DataTable.types';
import { useColumnResize } from './hooks/use-column-resize';

const SKELETON_ROWS = 25; // Generous fixed count — CSS clips during loading
const COPIED_RESET_MS = 2000;

export interface DataTableProps {
    columns: DataTableColDef[];
    rows: DataTableRow[];
    isLoading?: boolean;
    onRowClick?: OnRowClick;
    titleWhenNoResults?: string;
    messageWhenNoResults?: string;
    /**
     * Initial sort applied on first render. Honored only when the table is
     * uncontrolled (i.e. no `queryState` prop).
     */
    initialSort?: { field: string; direction?: SortDirection } | DataTableSort[];

    /**
     * When provided, the table switches to controlled mode: sorting,
     * filtering, and searching are delegated to the parent via
     * `onQueryStateChange` instead of happening client-side.
     */
    queryState?: DataTableQueryState;
    onQueryStateChange?: React.Dispatch<React.SetStateAction<DataTableQueryState>>;

    totalRows?: number;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;

    showSearch?: boolean;
    searchPlaceholder?: string;
    /** When true, removes the reserved scrollbar gutter space (useful when
     *  the table is rendered inside a non-scrolling container). */
    disableScrollbarGutter?: boolean;
    /** When provided, renders an expand/collapse chevron per row. The callback
     *  receives the row data and should return the expanded content. */
    renderExpandedRow?: (row: DataTableRow) => ReactNode;
}

const normalizeInitialSort = (input: DataTableProps['initialSort']): DataTableSort[] => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    return [{ field: input.field, direction: input.direction ?? SortDirection.Asc }];
};

const EXPAND_COLUMN_WIDTH = 40;

export const DataTable = ({
    columns,
    rows,
    isLoading = false,
    onRowClick,
    titleWhenNoResults,
    messageWhenNoResults,
    initialSort,
    queryState,
    onQueryStateChange,
    totalRows,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    showSearch = false,
    searchPlaceholder,
    disableScrollbarGutter = false,
    renderExpandedRow,
}: DataTableProps) => {
    const intl = useIntl();
    const { classes, cx } = useStyles();
    const resolvedNoResultsTitle = titleWhenNoResults ?? intl.formatMessage(i18n.noResultsTitle);
    const resolvedNoResultsMessage =
        messageWhenNoResults ?? intl.formatMessage(i18n.noResultsMessage);
    const copiedLabel = intl.formatMessage(bodyCellI18n.copied);
    const clickToCopyLabel = intl.formatMessage(bodyCellI18n.clickToCopy);

    const isExpandable = !!renderExpandedRow;
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

    const toggleExpand = useCallback((rowId: string | number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    }, []);

    const isControlled = queryState !== undefined;

    const [internalState, setInternalState] = useState<DataTableState>(() => ({
        ...DEFAULT_DATA_TABLE_STATE,
        sorts: normalizeInitialSort(initialSort),
    }));

    const effectiveSorts = queryState ? queryState.sorts : internalState.sorts;
    const effectiveFilters = queryState ? queryState.filters : internalState.filters;
    const effectiveSearch = queryState ? queryState.search : '';

    const queryStateRef = useRef<DataTableQueryState>({
        sorts: effectiveSorts,
        filters: effectiveFilters,
        search: effectiveSearch,
    });
    queryStateRef.current = {
        sorts: effectiveSorts,
        filters: effectiveFilters,
        search: effectiveSearch,
    };

    const emit = useCallback(
        (patch: Partial<DataTableQueryState>) => {
            if (isControlled) {
                onQueryStateChange?.((prev) => ({ ...prev, ...patch }));
                return;
            }
            setInternalState((prev) => {
                const next = { ...prev };
                if (patch.sorts !== undefined) next.sorts = patch.sorts;
                if (patch.filters !== undefined) next.filters = patch.filters;
                return next;
            });
        },
        [isControlled, onQueryStateChange],
    );

    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tableRef = useRef<HTMLTableElement>(null);
    const headerTableRef = useRef<HTMLTableElement>(null);
    const topScrollbarRef = useRef<HTMLDivElement>(null);
    const [scrollContentWidth, setScrollContentWidth] = useState(0);
    const [showTopScrollbar, setShowTopScrollbar] = useState(false);

    const { containerRef, activeResizeField, getColumnWidth, onResizeStart, onResizeHover } =
        useColumnResize(columns, isExpandable ? EXPAND_COLUMN_WIDTH : 0);

    useEffect(() => {
        return () => {
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);

    /**
     * Compound multi-sort cycle for a single column click:
     *   not sorted  → append { field, asc }
     *   sorted asc  → flip same entry to desc, keep its position
     *   sorted desc → remove entry; sort priority of remaining columns is preserved.
     */
    const handleSort = useCallback(
        (field: string) => {
            const current = queryStateRef.current.sorts;
            const existing = current.find((s) => s.field === field);
            let nextSorts: DataTableSort[];
            if (!existing) {
                nextSorts = [...current, { field, direction: SortDirection.Asc }];
            } else if (existing.direction === SortDirection.Asc) {
                nextSorts = current.map((s) =>
                    s.field === field ? { ...s, direction: SortDirection.Desc } : s,
                );
            } else {
                nextSorts = current.filter((s) => s.field !== field);
            }
            emit({ sorts: nextSorts });
        },
        [emit],
    );

    const handleCopy = useCallback(async (key: string, value: DataTableCellValue) => {
        try {
            await navigator.clipboard.writeText(toCellString(value));
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
            setCopiedKey(key);
            copiedTimerRef.current = setTimeout(() => {
                setCopiedKey(null);
                copiedTimerRef.current = null;
            }, COPIED_RESET_MS);
        } catch (error) {
            logUnknownException(error);
        }
    }, []);

    const handleFilterChange = useCallback(
        (field: string, values: DataTableCellValue[]) => {
            const column = columns.find((c) => c.field === field);
            const optionCount = column?.filterOptions?.length ?? 0;
            const nextFilters = { ...queryStateRef.current.filters };
            if (optionCount > 0 && values.length === optionCount) {
                delete nextFilters[field];
            } else {
                nextFilters[field] = values;
            }
            emit({ filters: nextFilters });
        },
        [columns, emit],
    );

    const handleClearAllFilters = useCallback(() => {
        emit({ filters: {} });
    }, [emit]);

    const handleSearchChange = useCallback(
        (next: string) => {
            emit({ search: next });
        },
        [emit],
    );

    const filteredRows = useMemo<DataTableRow[]>(() => {
        if (isControlled) return rows;
        const filterEntries = Object.entries(effectiveFilters);
        if (filterEntries.length === 0) return rows;

        const normalize = (v: DataTableCellValue): string =>
            v === null || v === undefined ? '' : String(v);
        const normalizedFilters: [string, Set<string>][] = filterEntries.map(([field, vals]) => [
            field,
            new Set(vals.map(normalize)),
        ]);

        return rows.filter((row) =>
            normalizedFilters.every(
                ([field, vals]) => vals.size > 0 && vals.has(normalize(row[field])),
            ),
        );
    }, [isControlled, rows, effectiveFilters]);

    const sortedRows = useMemo<DataTableRow[]>(() => {
        if (isControlled) return filteredRows;
        if (effectiveSorts.length === 0) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            for (const { field, direction } of effectiveSorts) {
                const aStr = toCellString(a[field]);
                const bStr = toCellString(b[field]);
                const cmp = aStr.localeCompare(bStr, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
                if (cmp !== 0) return direction === SortDirection.Asc ? cmp : -cmp;
            }
            return 0;
        });
    }, [isControlled, filteredRows, effectiveSorts]);

    useLayoutEffect(() => {
        const table = tableRef.current;
        const container = containerRef.current;
        if (!table || !container) return;
        const measure = () => {
            // Use getBoundingClientRect for subpixel-accurate widths at non-100% zoom
            const tableWidth = table.getBoundingClientRect().width;
            const containerWidth = container.getBoundingClientRect().width;
            // Add 1px buffer to the inner scrollbar div to guarantee scroll travel
            setScrollContentWidth(Math.ceil(tableWidth) + 1);
            setShowTopScrollbar(tableWidth - containerWidth > 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(table);
        ro.observe(container);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const topBar = topScrollbarRef.current;
        const bodyTable = tableRef.current;
        if (!topBar || !bodyTable) return;
        const onTopScroll = () => {
            const x = `translateX(-${topBar.scrollLeft}px)`;
            bodyTable.style.transform = x;
            const headerTable = headerTableRef.current;
            if (headerTable) headerTable.style.transform = x;
        };
        topBar.addEventListener('scroll', onTopScroll, { passive: true });
        return () => topBar.removeEventListener('scroll', onTopScroll);
    }, [showTopScrollbar]);

    useEffect(() => {
        const container = containerRef.current;
        const topBar = topScrollbarRef.current;
        if (!container || !topBar) return;
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) return;
            let deltaX = e.deltaX;
            if (e.shiftKey && deltaX === 0) deltaX = e.deltaY;
            if (Math.abs(deltaX) < 1) return;
            if (e.deltaMode === 1) deltaX *= 16;
            else if (e.deltaMode === 2) deltaX *= 100;
            const { scrollLeft, scrollWidth, clientWidth } = topBar;
            const atLeft = scrollLeft <= 0 && deltaX < 0;
            const atRight = scrollLeft >= scrollWidth - clientWidth - 1 && deltaX > 0;
            if (atLeft || atRight) return;
            e.preventDefault();
            topBar.scrollLeft += deltaX;
        };
        container.addEventListener('wheel', onWheel, { passive: false });
        return () => container.removeEventListener('wheel', onWheel);
    }, [showTopScrollbar]);

    // Memoized serialized keys so the scroll-reset effect fires on value
    // changes (not parent-identity changes) without restringifying on every render.
    const sortsKey = useMemo(() => JSON.stringify(effectiveSorts), [effectiveSorts]);
    const filtersKey = useMemo(() => JSON.stringify(effectiveFilters), [effectiveFilters]);

    useEffect(() => {
        if (!isControlled) return;
        const container = containerRef.current;
        if (container) container.scrollTop = 0;
    }, [isControlled, sortsKey, filtersKey, effectiveSearch]);

    const { containerRef: scrollContainerRef, onScroll: handleInfiniteScroll } = useInfiniteScroll({
        hasNextPage: !!hasMore,
        isFetchingNextPage: !!isLoadingMore,
        fetchNextPage: () => onLoadMore?.(),
    });

    // Skeleton only when truly empty + loading (no stale-data flash on refetch).
    const showSkeleton = isLoading && sortedRows.length === 0;
    const isEmpty = !showSkeleton && sortedRows.length === 0;

    const shellByField = useMemo(() => {
        const map: Record<string, DataTableCellShell> = {};
        columns.forEach((column, index) => {
            map[column.field] = {
                column,
                isFirst: index === 0,
                isLast: index === columns.length - 1,
                width: getColumnWidth(column),
                isResizeActive: activeResizeField === column.field,
                onResizeStart,
                onResizeHover,
            };
        });
        return map;
    }, [columns, getColumnWidth, activeResizeField, onResizeStart, onResizeHover]);

    return (
        <Box className={classes.root}>
            <DataTableFilterBar
                columns={columns}
                filters={effectiveFilters}
                onChange={handleFilterChange}
                onClearAll={handleClearAllFilters}
                showSearch={showSearch}
                searchValue={effectiveSearch}
                onSearchChange={handleSearchChange}
                searchPlaceholder={searchPlaceholder}
                totalLabel={
                    totalRows !== undefined && sortedRows.length > 0
                        ? intl.formatMessage(i18n.loadedOfTotal, {
                              loaded: sortedRows.length,
                              total: totalRows,
                          })
                        : undefined
                }
            />
            {showTopScrollbar && !showSkeleton && (
                <div ref={topScrollbarRef} className={classes.topScrollbar}>
                    <div style={{ width: scrollContentWidth }} />
                </div>
            )}
            <Box className={classes.tableScrollArea}>
                {/* Header sits outside the scroll container so the vertical
                    scrollbar starts below it. Both tables share column widths
                    via `shellByField` so the rows align with the header. */}
                <Box
                    className={cx(
                        classes.headerViewport,
                        showTopScrollbar && !showSkeleton && classes.headerViewportFlat,
                    )}
                    style={disableScrollbarGutter ? { paddingRight: 0 } : undefined}
                >
                    <Table ref={headerTableRef} size="small" className={classes.table}>
                        <TableHead>
                            <TableRow>
                                {isExpandable && (
                                    <TableCell
                                        className={cx(
                                            classes.cell,
                                            classes.headerCell,
                                            classes.expandCell,
                                            classes.expandCellSize,
                                        )}
                                    />
                                )}
                                {columns.map((column) => {
                                    const sort = effectiveSorts.find(
                                        (s) => s.field === column.field,
                                    );
                                    return (
                                        <DataTableHeaderCell
                                            key={column.field}
                                            shell={shellByField[column.field]}
                                            isSortActive={sort !== undefined}
                                            sortDirection={sort?.direction ?? SortDirection.Asc}
                                            onSort={handleSort}
                                        />
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                    </Table>
                </Box>
                <TableContainer
                    ref={(node: HTMLDivElement | null) => {
                        containerRef.current = node;
                        scrollContainerRef(node);
                    }}
                    onScroll={handleInfiniteScroll}
                    className={cx(
                        classes.bodyViewport,
                        showSkeleton && classes.bodyViewportLoading,
                    )}
                    style={disableScrollbarGutter ? { scrollbarGutter: 'auto' } : undefined}
                >
                    <Table ref={tableRef} size="small" className={classes.table}>
                        <TableBody>
                            {showSkeleton && (
                                <TableSkeleton
                                    rows={SKELETON_ROWS}
                                    columns={columns.length + (isExpandable ? 1 : 0)}
                                />
                            )}
                            {isEmpty && (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length + (isExpandable ? 1 : 0)}
                                        className={classes.emptyStateCell}
                                    >
                                        <EmptyState
                                            title={resolvedNoResultsTitle}
                                            message={resolvedNoResultsMessage}
                                            variant="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!showSkeleton &&
                                sortedRows.map((row) => {
                                    const isExpanded = expandedRows.has(row.id);
                                    return (
                                        <Fragment key={row.id}>
                                            <TableRow
                                                className={cx(
                                                    classes.row,
                                                    !!onRowClick &&
                                                        !isExpandable &&
                                                        classes.rowClickable,
                                                    isExpandable && classes.rowExpandable,
                                                    isExpanded && classes.rowExpanded,
                                                )}
                                                onClick={
                                                    isExpandable
                                                        ? () => toggleExpand(row.id)
                                                        : undefined
                                                }
                                            >
                                                {isExpandable && (
                                                    <TableCell
                                                        className={cx(
                                                            classes.cell,
                                                            classes.expandCell,
                                                            classes.expandCellSize,
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            className={cx(
                                                                classes.expandIcon,
                                                                isExpanded &&
                                                                    classes.expandIconOpen,
                                                            )}
                                                            aria-expanded={isExpanded}
                                                            aria-label={intl.formatMessage(
                                                                isExpanded
                                                                    ? i18n.collapseRow
                                                                    : i18n.expandRow,
                                                            )}
                                                        >
                                                            <ChevronRightIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                                {columns.map((column) => {
                                                    const cellKey = `${row.id}-${column.field}`;
                                                    return (
                                                        <DataTableBodyCell
                                                            key={cellKey}
                                                            shell={shellByField[column.field]}
                                                            row={row}
                                                            cellKey={cellKey}
                                                            isCopied={copiedKey === cellKey}
                                                            copiedLabel={copiedLabel}
                                                            clickToCopyLabel={clickToCopyLabel}
                                                            onCopy={handleCopy}
                                                            onRowClick={
                                                                isExpandable
                                                                    ? undefined
                                                                    : onRowClick
                                                            }
                                                        />
                                                    );
                                                })}
                                            </TableRow>
                                            {isExpandable && isExpanded && (
                                                <TableRow className={classes.expandedContentRow}>
                                                    <TableCell
                                                        colSpan={columns.length + 1}
                                                        className={classes.expandedContentCell}
                                                    >
                                                        <Box className={classes.expandedContent}>
                                                            {renderExpandedRow?.(row)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            {isLoadingMore && hasMore && (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length + (isExpandable ? 1 : 0)}
                                        className={classes.loadingMoreCell}
                                    >
                                        <Box className={classes.loadingMoreInner}>
                                            <CircularProgress size={16} />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};
